/**
 * OC Recruitment — Individual Sector Landing Page
 * SEO-targeted pages for each packaging sector
 * Each page targets specific keywords like "flexible packaging recruiter USA"
 */

import { Link, useParams } from "wouter";
import { ArrowRight, Target, Users, TrendingUp, Shield, Briefcase, Building2 } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQ from "@/components/FAQ";
import NotFound from "@/pages/NotFound";

import { getSectorBySlug, getAllSectors } from "@/lib/sectorData";
export { getSectorBySlug, getAllSectors } from "@/lib/sectorData";

export default function SectorDetail() {
  const { slug } = useParams<{ slug: string }>();
  const sector = slug ? getSectorBySlug(slug) : undefined;

  if (!sector) {
    return <NotFound />;
  }

  return (
    <div>
      <SEO
        title={sector.metaTitle}
        description={sector.metaDescription}
        keywords={sector.keywords}
        canonical={`/sectors/${sector.slug}`}
        ogImage={sector.image}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${sector.name} Sales Recruitment`,
          description: sector.metaDescription,
          provider: {
            "@type": "Organization",
            name: "OC Recruitment",
            url: "https://oc-international.com",
          },
          areaServed: {
            "@type": "Country",
            name: "United States",
          },
          serviceType: `${sector.name} Sales Recruitment`,
        }}
      />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={sector.image}
            alt={`${sector.name} sales recruitment - OC Recruitment`}
            width={1600}
            height={1200}
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137]/92 via-[#0F2137]/80 to-[#0F2137]/55" />
        </div>
        <div className="container relative z-10 pt-32 pb-20">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Sectors", href: "/packaging-sectors" },
              { name: sector.name },
            ]}
            className="mb-6"
          />
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                {sector.heroSubtitle}
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                {sector.heroTitle}
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                {sector.heroDescription}
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all duration-200 group"
              >
                Discuss a {sector.name} Search
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-4xl">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-8">
                {sector.name} Sales Recruitment
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-slate-brand text-lg leading-relaxed">
                {sector.overview}
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="container">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-10">
              {sector.rolesTitle}
            </h2>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sector.roles.map((role, i) => (
              <ScrollReveal key={role} delay={i * 0.05}>
                <div className="bg-white rounded border border-border p-5 hover:border-steel/30 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3">
                    <Briefcase size={18} className="text-steel mt-0.5 flex-shrink-0" />
                    <span className="text-midnight text-sm font-medium">{role}</span>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <ScrollReveal>
            <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              Industry Challenges
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl text-midnight mb-10">
              Why {sector.name} Sales Recruitment Is Difficult
            </h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-8">
            {sector.challenges.map((challenge, i) => {
              const icons = [Target, Users, TrendingUp];
              const Icon = icons[i % icons.length];
              return (
                <ScrollReveal key={challenge.title} delay={i * 0.1}>
                  <div className="bg-[#F8F9FA] rounded p-8 h-full">
                    <Icon size={28} className="text-steel mb-4" />
                    <h3 className="text-midnight font-semibold text-lg mb-3">{challenge.title}</h3>
                    <p className="text-slate-brand text-sm leading-relaxed">{challenge.description}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why OC */}
      <section className="py-20 lg:py-28 bg-[#0F2137]">
        <div className="container">
          <div className="max-w-4xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Why OC Recruitment
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="text-3xl sm:text-4xl text-white mb-8">
                Your {sector.name} Recruitment Partner
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-white/70 text-lg leading-relaxed mb-10">
                {sector.whyOC}
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all duration-200 group"
                >
                  Start a {sector.name} Search
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  href="/for-companies"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded hover:bg-white/10 transition-all duration-200"
                >
                  Learn About Our Process
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <FAQ
            title={`${sector.name} Recruitment FAQ`}
            items={sector.faqs}
          />
        </div>
      </section>

      {/* Cross-links to other sectors */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="container">
          <ScrollReveal>
            <h2 className="text-2xl text-midnight mb-8">Other Packaging Sectors We Recruit For</h2>
          </ScrollReveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {getAllSectors()
              .filter((s) => s.slug !== sector.slug)
              .map((s, i) => (
                <ScrollReveal key={s.slug} delay={i * 0.05}>
                  <Link
                    href={`/sectors/${s.slug}`}
                    className="block bg-white rounded border border-border p-4 text-center hover:border-steel/30 hover:shadow-sm transition-all"
                  >
                    <span className="text-midnight text-sm font-medium">{s.name}</span>
                  </Link>
                </ScrollReveal>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
